/**
 * Responsibility: describe the default aircraft as a wireframe vertex and edge list.
 *
 * Plain data, no Three.js. Hand-authored in code rather than loaded from a model file,
 * which keeps the whole aircraft reviewable in a diff and means there is no asset pipeline
 * to maintain for a shape with a dozen edges.
 *
 * ## Coordinate convention
 *
 * Model space matches Three.js object space: **nose points down negative Z**, +Y is up,
 * +X is the right wing. Roughly two units long, scaled to world size by `AIRCRAFT.SCALE`,
 * so the silhouette can be edited here without anyone having to think about world units.
 *
 * Task 10 adds the registry and the remaining models, including the invisible one. This
 * file is already in the shape that registry expects.
 */

/**
 * A simple delta. Chosen because a swept triangle reads clearly as an aircraft from behind
 * and above, which is the only angle the chase camera ever shows, and because a low edge
 * count keeps the wireframe from competing with the terrain for attention.
 */
export const defaultAircraft = {
  id: 'default',
  label: 'Delta',

  /** Vertex positions as flat [x, y, z] triples. */
  vertices: [
    [0, 0, -1.15], // 0 nose
    [-0.85, 0, 0.5], // 1 left wingtip
    [0.85, 0, 0.5], // 2 right wingtip
    [0, 0, 0.35], // 3 tail root, slightly forward of the trailing edge
    [0, 0.42, 0.6], // 4 top of the fin
    [0, -0.16, 0.15], // 5 belly, giving the hull some depth from behind
  ],

  /** Edges as index pairs into `vertices`. */
  edges: [
    // Planform: nose to each wingtip, and the trailing edge between them.
    [0, 1],
    [0, 2],
    [1, 3],
    [2, 3],
    // Spine, so the shape does not read as a flat triangle when banked.
    [0, 3],
    // Vertical fin.
    [3, 4],
    [0, 4],
    // Belly ridge, which is what actually communicates roll from directly behind.
    [0, 5],
    [1, 5],
    [2, 5],
  ],
};
